import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import http from '../utils/http';
import { AfrVersionStateChange } from "./AfrSlice";
import { AssignmentEvent, CommentEvent, EmailEventInput, MessageEvent } from "./EventsSlice";
import { RootState } from './store';
import { TaxonomyTypeEnum } from './TaxonomyVersionSlice';

export interface AfrReview {
    connector: string;
    entityName: string;
    entityType: string;
    afrReceivedDate: string | null;
    auditReceivedDate: string | null;
    verifiedDate: string | null
    afrYear: number;
    daysToAssign: number;
    daysToVerify: number;
    readyForReview: string;
    assignments: Array<AssignmentEvent>;
    comments: Array<CommentEvent>;
    firstDiscrepancy: MessageEvent | null;
    secondDiscrepancy: MessageEvent | null;
    thirdDiscrepancy: MessageEvent | null;
    filingStatus: AfrVersionStateChange | null;
    afrTypeInfo: string | null;
    isDirty: boolean;
}

export interface AfrReviewState {
    filteredResults: Array<AfrReview>;
    status: 'idle' | 'loading' | 'failed' | 'uninitialized';
}

const initialState: AfrReviewState = {
    filteredResults: [],
    status: "idle"
}

export interface ReviewsRequestFilter {
    entities: Array<string>;
    versionStates: Array<string>;
    startYear: number;
    endYear: number;
    entityTypes: Array<string>;
    reviewers: Array<string>;
    taxonomyType: TaxonomyTypeEnum;
}


export interface AfrAssignmentRequest {
    assigneeId: string;
    afrVersionId: string;
}

export interface PostCommentRequest {
    comment: string;
    afrVersionId: string;
    role: string;
}

const matchAfrReferences = function(a: string, b: string)
{
    const idxa = a.lastIndexOf(",");
    if (idxa < 0) {
        return false;
    }
    const idxb = b.lastIndexOf(",");
    if (idxb < 0) {
        return false;
    }
    return a.substring(0, idxa) === b.substring(0, idxb);
}

export const loadReviews = createAsyncThunk(
    "AfrReview/all",
    async (reviewFilter: ReviewsRequestFilter): Promise<Array<AfrReview>> => {
        try {
            const result = await http<ReviewsRequestFilter, Array<AfrReview>>({
                path: `/afrreview/getReviews`,
                method: 'post',
                body: reviewFilter
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            return [];
        }
    });

export const postAssignment = createAsyncThunk(
    "Events/PostAssignment",
    async (req: AfrAssignmentRequest): Promise<AssignmentEvent | null> => {
        try {
            const result = await http<AfrAssignmentRequest, AssignmentEvent>({
                path: `/events/postAssignment`,
                method: 'post',
                body: req
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const postComment = createAsyncThunk(
    "Events/PostComment",
    async (req: PostCommentRequest): Promise<CommentEvent | null> => {
        try {
            const result = await http<PostCommentRequest, CommentEvent>({
                path: `/events/postComment`,
                method: 'post',
                body: req
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const postDiscrepancy = createAsyncThunk(
    "Events/PostEmail",
    async (mailInput: EmailEventInput): Promise<MessageEvent | null> => {
        try {
            const result = await http<EmailEventInput, MessageEvent>({
                path: `/events/postEmail`,
                method: 'post',
                body: mailInput
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });


export const afrReviewSlice = createSlice({
    name: 'AfrReviews',
    initialState,
    reducers: {
        resetState: (state: any, action: any) => {
            return initialState
        },
        updateReview: (state: any, action: any) => {

           
        }
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(loadReviews.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadReviews.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.filteredResults = action.payload;
            })
            .addCase(postDiscrepancy.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(postDiscrepancy.fulfilled, (state: AfrReviewState, action: any) => {
                state.status = "idle";
                if (action.payload && state.filteredResults) {
                    var discrepancy: MessageEvent = action.payload;
                    for (var review of state.filteredResults) {
                        if (matchAfrReferences(review.connector, discrepancy.connector)) {
                            if (!review.firstDiscrepancy) {
                                review.firstDiscrepancy = discrepancy;
                            } else if (!review.secondDiscrepancy) {
                                review.secondDiscrepancy = discrepancy;
                            } else {
                                review.thirdDiscrepancy = discrepancy;
                            }
                        }
                    }
                }
            })
            .addCase(postAssignment.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(postAssignment.fulfilled, (state: AfrReviewState, action: any) => {
                state.status = "idle";
                if (action.payload && state.filteredResults) {
                    var assignment: AssignmentEvent = action.payload;
                    for (var review of state.filteredResults) {
                        if (matchAfrReferences(review.connector,assignment.connector)) {
                            review.assignments.push(assignment);
                        }
                    }
                }
            })
            .addCase(postComment.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(postComment.fulfilled, (state: AfrReviewState, action: any) => {
                state.status = "idle";
                if (action.payload && state.filteredResults) {
                    var comment: CommentEvent = action.payload;            
                    for (var review of state.filteredResults) {
                        if (matchAfrReferences(review.connector, comment.connector)) {
                            review.comments.push(comment);
                        }
                    }
                }
            })
    }
});



export const { resetState } = afrReviewSlice.actions;

export const selectReviews = (state: RootState) => state.reviews ? state.reviews.filteredResults : [];

export const selectAfrReviewLoadingStatus = (state: RootState) => state.reviews ? state.reviews.status : 'uninitialized';

export default afrReviewSlice.reducer;
